import { Ledger } from "@common/contracts/ledger.contract";
import { Account } from "@common/types/domains/accounting/account";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { getEntityIdForGLAccount } from "@hooks/useEntityIdForGLAccount";
import { findPrimaryLedgerId } from "@lib/accountOverrides";
import { MatchableTransaction } from "../types";

export interface TransactionLedgerContext {
    entityId: number | undefined;
    hasAccrualLedger: boolean;
    primaryAccrualLedgerId: string | undefined;
    primaryCashLedgerId: string | undefined;
    accounts: Account[];
}

export type TransactionLedgerContexts = ReadonlyMap<
    number,
    TransactionLedgerContext
>;

interface EntityRef {
    id: number;
    isBusiness: boolean;
}
type LedgersByEntityId = Record<number, Ledger[]>;

function hasAccrualLedger(ledgers: Ledger[] | undefined): boolean {
    return (
        ledgers?.some(
            (ledger) => ledger.basis === AccountingLedgerBasis.accruals,
        ) ?? false
    );
}

export function getGlEntityIds({
    transactions,
    entities,
}: {
    transactions: readonly MatchableTransaction[];
    entities: EntityRef[];
}): number[] {
    const entityIds = transactions.flatMap((transaction) => {
        const entityId = getEntityIdForGLAccount(transaction, entities);
        return entityId === undefined ? [] : [entityId];
    });
    return [...new Set(entityIds)];
}

export function getAccrualEntityIds({
    entityIds,
    ledgersByEntityId,
}: {
    entityIds: readonly number[];
    ledgersByEntityId: LedgersByEntityId | undefined;
}): number[] {
    return entityIds.filter((entityId) =>
        hasAccrualLedger(ledgersByEntityId?.[entityId]),
    );
}

/**
 * Each transaction books to the chart and ledgers of its own entity, so a
 * document matched to transactions from several entities needs one context
 * per transaction rather than one taken from the first.
 */
export function buildTransactionLedgerContexts({
    transactions,
    entities,
    ledgersByEntityId,
    accountsByEntityId,
}: {
    transactions: readonly MatchableTransaction[];
    entities: EntityRef[];
    ledgersByEntityId: LedgersByEntityId | undefined;
    accountsByEntityId: Record<number, Account[]>;
}): Map<number, TransactionLedgerContext> {
    return new Map(
        transactions.map((transaction) => {
            const entityId = getEntityIdForGLAccount(transaction, entities);
            const ledgers =
                entityId === undefined
                    ? undefined
                    : ledgersByEntityId?.[entityId];
            const isAccrual = hasAccrualLedger(ledgers);
            const accounts =
                entityId !== undefined && isAccrual
                    ? (accountsByEntityId[entityId] ?? []).filter(
                          (account) => !account.isDisabled,
                      )
                    : [];
            return [
                transaction.id,
                {
                    entityId,
                    hasAccrualLedger: isAccrual,
                    primaryAccrualLedgerId: findPrimaryLedgerId(
                        ledgers,
                        AccountingLedgerBasis.accruals,
                    ),
                    primaryCashLedgerId: findPrimaryLedgerId(
                        ledgers,
                        AccountingLedgerBasis.cash,
                    ),
                    accounts,
                },
            ];
        }),
    );
}
